import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { toast } from "sonner";
import { Phone, MapPin, Mail, Send, Clock } from "lucide-react";

export default function Iletisim() {
  useSEO({
    title: "İletişim",
    description: "YK İnşaat Müteahhitlik iletişim bilgileri: telefon, adres, e-posta ve online iletişim formu.",
    path: "/iletisim",
  });
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error("Ad ve mesaj alanları zorunludur");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Mesajınız iletildi. En kısa sürede dönüş yapacağız.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Mesaj gönderilemedi. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A]" data-testid="iletisim-page">
      <section className="py-24 lg:py-32 border-b border-[#1F2937]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">İletişim</div>
          <h1 className="font-heading text-4xl lg:text-6xl text-white tracking-[0.05em] leading-tight max-w-3xl">
            Bize<br />
            <span className="text-[#C9A961]">Ulaşın</span>
          </h1>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 space-y-6">
            <div className="yk-card p-8">
              <Phone className="w-8 h-8 text-[#C9A961] mb-4" strokeWidth={1.2} />
              <div className="section-label mb-2">Telefon</div>
              <a href="tel:05449751294" className="font-heading text-2xl text-white hover:text-[#C9A961] tracking-wider block">
                {stats?.phone || "0544 975 12 94"}
              </a>
            </div>
            <div className="yk-card p-8">
              <MapPin className="w-8 h-8 text-[#C9A961] mb-4" strokeWidth={1.2} />
              <div className="section-label mb-2">Adres</div>
              <div className="text-white leading-relaxed">{stats?.address || "Şarhöyük mahallesi bülbüller Sokak no 50/B"}</div>
            </div>
            <div className="yk-card p-8">
              <Mail className="w-8 h-8 text-[#C9A961] mb-4" strokeWidth={1.2} />
              <div className="section-label mb-2">E-posta</div>
              <a href={`mailto:${stats?.email || "info@ykinsaat.com"}`} className="text-white hover:text-[#C9A961]">{stats?.email || "info@ykinsaat.com"}</a>
            </div>
            <div className="yk-card p-8">
              <Clock className="w-8 h-8 text-[#C9A961] mb-4" strokeWidth={1.2} />
              <div className="section-label mb-2">Çalışma Saatleri</div>
              <div className="text-white/70 text-sm">Pazartesi - Cumartesi: 08:30 - 18:30</div>
              <div className="text-white/70 text-sm">Pazar: Kapalı</div>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-3 yk-card p-8 lg:p-12 space-y-5" data-testid="contact-form">
            <div>
              <div className="section-label mb-3">Bize Yazın</div>
              <h2 className="font-heading text-2xl lg:text-3xl text-white tracking-[0.05em]">Projeniz için görüşelim</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Ad Soyad *</label>
                <input type="text" className="yk-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Telefon</label>
                <input type="tel" className="yk-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">E-posta</label>
                <input type="email" className="yk-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Konu</label>
                <input type="text" className="yk-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Mesaj *</label>
              <textarea rows={6} className="yk-input resize-none" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" required />
            </div>

            <button type="submit" className="btn-gold w-full disabled:opacity-50" disabled={loading} data-testid="contact-submit">
              {loading ? "Gönderiliyor..." : "Mesajı Gönder"}
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
